import axios from "axios";

// Helper-e pentru workspace-ul CRM -> Email Marketing (analytics read-only peste
// datele Mailjet). Vezi docs/exec-plans/active/2026-07-04-email-marketing-workspace.md.

export const EMAIL_MARKETING_PERIODS = Object.freeze([
  { value: "30", labelKey: "ncrm.emailMarketing.periods.days30" },
  { value: "90", labelKey: "ncrm.emailMarketing.periods.days90" },
  { value: "365", labelKey: "ncrm.emailMarketing.periods.days365" },
  { value: "all", labelKey: "ncrm.emailMarketing.periods.all" },
]);

const buildQuery = ({ accountId, period } = {}) => {
  const params = {};
  if (accountId) params.accountId = accountId;
  if (period && period !== "all") params.days = period;
  return params;
};

export const fetchEmailMarketingOverview = async ({ accountId, period } = {}) => {
  const { data } = await axios.get("/ncrm/email-marketing/overview", {
    params: buildQuery({ accountId, period }),
  });
  return data;
};

export const fetchEmailMarketingCampaigns = async ({ accountId, period, limit = 200, offset = 0 } = {}) => {
  const { data } = await axios.get("/ncrm/email-marketing/campaigns", {
    params: { ...buildQuery({ accountId, period }), limit, offset },
  });
  return data;
};

export const triggerEmailMarketingSync = async ({ accountId } = {}) => {
  const payload = accountId ? { accountId } : {};
  const { data } = await axios.post("/ncrm/email-marketing/sync", payload);
  return data;
};

// Pivotează timeline-ul brut din API ({day, event_type, events_count}) în serii
// pentru chart-ul de bare (open/click pe zi). Peste `maxBars` zile distincte,
// agregăm pe săptămâni (luni ca început) ca să rămână lizibil pe 90/365 zile.
export const buildTimelineSeries = (timelineRows = [], { maxBars = 31 } = {}) => {
  const byDay = new Map();
  (timelineRows || []).forEach((row) => {
    const day = String(row.day || "").slice(0, 10);
    if (!day) return;
    if (!byDay.has(day)) byDay.set(day, { day, open: 0, click: 0 });
    const bucket = byDay.get(day);
    const count = Number(row.events_count) || 0;
    if (row.event_type === "open") bucket.open += count;
    if (row.event_type === "click") bucket.click += count;
  });
  let days = Array.from(byDay.values()).sort((a, b) => (a.day < b.day ? -1 : 1));
  let aggregatedBy = "day";
  if (days.length > maxBars) {
    const byWeek = new Map();
    days.forEach((entry) => {
      const date = new Date(`${entry.day}T00:00:00`);
      const weekday = (date.getDay() + 6) % 7; // luni = 0
      date.setDate(date.getDate() - weekday);
      // Pastram calendarul local: `toISOString()` converteste in UTC si, in
      // timezone-ul aplicatiei (UTC+02/+03), ar muta luni in duminica anterioara.
      const weekStart = [
        date.getFullYear(),
        String(date.getMonth() + 1).padStart(2, "0"),
        String(date.getDate()).padStart(2, "0"),
      ].join("-");
      if (!byWeek.has(weekStart)) byWeek.set(weekStart, { day: weekStart, open: 0, click: 0 });
      const bucket = byWeek.get(weekStart);
      bucket.open += entry.open;
      bucket.click += entry.click;
    });
    days = Array.from(byWeek.values()).sort((a, b) => (a.day < b.day ? -1 : 1));
    aggregatedBy = "week";
  }
  const hasData = days.some((entry) => entry.open > 0 || entry.click > 0);
  return { days, aggregatedBy, hasData };
};

export const EMAIL_MARKETING_PLAN_STATUSES = Object.freeze([
  "draft",
  "planned",
  "scheduled",
  "sent",
  "analyzed",
  "archived",
]);

export const resolvePlanStatusColor = (status) => {
  switch (status) {
    case "planned":
    case "scheduled":
      return "info";
    case "sent":
      return "success";
    case "analyzed":
      return "secondary";
    case "archived":
      return "neutral";
    default:
      return "neutral";
  }
};

export const fetchEmailMarketingPlans = async ({ status, category } = {}) => {
  const params = {};
  if (status) params.status = status;
  if (category) params.category = category;
  const { data } = await axios.get("/ncrm/email-marketing/plans", { params });
  return data.plans || [];
};

export const fetchEmailMarketingPlanCategories = async () => {
  const { data } = await axios.get("/ncrm/email-marketing/plans/categories");
  return data.categories || [];
};

export const createEmailMarketingPlan = async (payload) => {
  const { data } = await axios.post("/ncrm/email-marketing/plans", payload);
  return data.plan;
};

export const updateEmailMarketingPlan = async (planId, payload) => {
  const { data } = await axios.patch(`/ncrm/email-marketing/plans/${planId}`, payload);
  return data.plan;
};

export const deleteEmailMarketingPlan = async (planId) => {
  await axios.delete(`/ncrm/email-marketing/plans/${planId}`);
};

export const formatPercent = (value, { digits = 1 } = {}) => {
  if (value === null || value === undefined || value === "") return "—";
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "—";
  return `${(parsed * 100).toFixed(digits)}%`;
};

export const resolveScoreColor = (score) => {
  if (score === null || score === undefined || score === "") return "neutral";
  const parsed = Number(score);
  if (!Number.isFinite(parsed)) return "neutral";
  if (parsed >= 70) return "success";
  if (parsed >= 40) return "warning";
  return "error";
};

const formatDateForExport = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  return `${dd}.${mm}.${date.getFullYear()}`;
};

const exportRow = (campaign) => ({
  subject: campaign.subject || "",
  account: campaign.account_name || "",
  list: campaign.list_name || "",
  sentAt: formatDateForExport(campaign.sent_at),
  recipients: Number(campaign.recipients_count) || 0,
  delivered: Number(campaign.delivered_count) || 0,
  opened: Number(campaign.opened_count) || 0,
  clicked: Number(campaign.clicked_count) || 0,
  bounced: Number(campaign.bounced_count) || 0,
  unsubscribed: Number(campaign.unsubscribed_count) || 0,
  openRate: campaign.rates?.openRate ?? null,
  clickRate: campaign.rates?.clickRate ?? null,
  clickToOpenRate: campaign.rates?.clickToOpenRate ?? null,
  bounceRate: campaign.rates?.bounceRate ?? null,
  unsubRate: campaign.rates?.unsubRate ?? null,
  leadsAdded: Number(campaign.leadsAdded) || 0,
  dealsCount: Number(campaign.dealsCount) || 0,
  contractsCount: Number(campaign.contractsCount) || 0,
  score: campaign.score ?? null,
  atRisk: Boolean(campaign.atRisk),
  metricsSource: campaign.metricsSource || "counters",
});

// Export markdown gandit pentru a fi dat unui LLM spre analiza (cerinta MVP):
// tabel compact + context despre semnificatia coloanelor.
export const buildCampaignsMarkdownExport = ({ campaigns = [], overview = null, generatedAt = new Date() } = {}) => {
  const lines = [];
  lines.push(`# Email marketing report (${formatDateForExport(generatedAt)})`);
  lines.push("");
  if (overview?.totals) {
    const t = overview.totals;
    lines.push(
      `Totals: campaigns=${t.campaignsCount}, delivered=${t.delivered}, opened=${t.opened}, clicked=${t.clicked}, bounced=${t.bounced}, unsubscribed=${t.unsubscribed}`
    );
  }
  if (overview?.funnel) {
    const f = overview.funnel;
    lines.push(
      `Funnel: engaged_contacts=${f.engagedContacts}, leads_added=${f.leadsAdded}, deals=${f.dealsCount}, contracts=${f.contractsCount}`
    );
  }
  lines.push("");
  lines.push(
    "| Subject | Sent | Delivered | Open rate | Click rate | CTOR | Bounce | Unsub | Leads | Deals | Contracts | Score | At risk |"
  );
  lines.push("| --- | --- | --- | --- | --- | --- | --- | --- | --- | --- | --- | --- | --- |");
  let hasEstimatedRows = false;
  campaigns.forEach((campaign) => {
    const row = exportRow(campaign);
    const estimatedMark = row.metricsSource === "events" ? " \\*" : "";
    if (row.metricsSource === "events") hasEstimatedRows = true;
    lines.push(
      `| ${row.subject.replace(/\|/g, "/")}${estimatedMark} | ${row.sentAt} | ${row.delivered} | ${formatPercent(row.openRate)} | ${formatPercent(row.clickRate)} | ${formatPercent(row.clickToOpenRate)} | ${formatPercent(row.bounceRate)} | ${formatPercent(row.unsubRate)} | ${row.leadsAdded} | ${row.dealsCount} | ${row.contractsCount} | ${row.score ?? "—"} | ${row.atRisk ? "yes" : "no"} |`
    );
  });
  lines.push("");
  lines.push(
    "Notes: score is a 0-100 composite of open/click rates, generated leads, list health (bounce/unsub penalty) and deals conversion. Leads/deals/contracts are attributed last-touch via the Mailjet Engagement workflow."
  );
  if (hasEstimatedRows) {
    lines.push(
      "\\* metrics estimated from webhook events (official Mailjet stat counters not synced yet for this campaign); delivered is approximated by 'sent' events."
    );
  }
  return lines.join("\n");
};

export const buildCampaignsJsonExport = ({ campaigns = [], overview = null, generatedAt = new Date() } = {}) =>
  JSON.stringify(
    {
      generatedAt: new Date(generatedAt).toISOString(),
      totals: overview?.totals || null,
      funnel: overview?.funnel || null,
      campaigns: campaigns.map(exportRow),
    },
    null,
    2
  );
